/** Pure domain helpers shared by the Worker, seed, and tests. */

export const FLAKINESS_WINDOW = 20;
export const FLAKINESS_WATCHLIST = 25;

export const PRIORITIES = ["CRITICAL", "HIGH", "MEDIUM", "LOW"] as const;
export const SEVERITIES = ["BLOCKER", "CRITICAL", "MAJOR", "MINOR", "TRIVIAL"] as const;
export const CASE_STATUSES = ["DRAFT", "IN_REVIEW", "APPROVED", "DEPRECATED"] as const;
export const AUTOMATION = ["MANUAL", "TO_BE_AUTOMATED", "AUTOMATED"] as const;
export const TEST_TYPES = ["FUNCTIONAL", "REGRESSION", "SMOKE", "SECURITY", "API", "E2E"] as const;
export const EXEC_STATUSES = ["UNEXECUTED", "PASS", "FAIL", "WIP", "BLOCKED", "SKIPPED", "FLAKY"] as const;
export const TRIAGE = ["PRODUCT_BUG", "FLAKY_TEST", "SELECTOR_DRIFT", "ENV_ISSUE", "DATA_ISSUE"] as const;
export const FRAMEWORKS = ["PLAYWRIGHT", "CYPRESS", "JUNIT"] as const;

export type Priority = (typeof PRIORITIES)[number];
export type CaseStatus = (typeof CASE_STATUSES)[number];

export interface StepInput {
  order?: number;
  action: string;
  data?: string;
  test_data?: string;
  expected?: string;
  expected_result?: string;
  shared_step_id?: string | null;
  called_case_id?: string | null;
  called_test_case_id?: string | null;
}

export interface Step {
  order: number;
  action: string;
  data: string;
  expected: string;
  shared_step_id: string | null;
  called_case_id: string | null;
}

export interface ExpandedStep {
  step_order: number;
  status: string;
  actual_result: string;
  attachments: string[];
  action: string;
  data: string;
  expected: string;
  source: "case" | "shared" | "called";
  shared_step_id?: string;
  shared_step_version?: number;
  called_test_case_id?: string;
}

export interface FlakeRun {
  status: string;
  environment: string;
  git_commit_sha: string | null;
}

export interface DiffRow {
  order: number;
  change: "same" | "added" | "deleted" | "modified";
  left: Step | null;
  right: Step | null;
}

/**
 * Rolling flakiness score, 0–100.
 *
 * The spec's original formula was truncated. This is the recovered rule:
 * - N = 20. Callers pass at most the last 20 PASS/FAIL outcomes, oldest first.
 * - A consecutive pair counts as a transition only when both runs share an environment
 *   and the statuses are a PASS↔FAIL flip.
 * - Weight is 1.5 when that pair also shares a non-empty git SHA (retry on the same commit)
 *   and 1.0 otherwise.
 * - score = clamp(weightedFlips / (sampleSize - 1) * 100, 0, 100).
 *   sampleSize is the number of PASS/FAIL rows in the window, so the denominator is the
 *   spec's (N − 1) when the window is full and the observed pair count when it is not.
 * - Rows that are not PASS or FAIL are ignored before pairing.
 * - Cross-environment pairs are not flips and are not part of sampleSize.
 * - Score above {@link FLAKINESS_WATCHLIST} is the Flakiness Watchlist.
 */
export function flakinessScore(runs: FlakeRun[]): number {
  const sample = runs.filter((run) => run.status === "PASS" || run.status === "FAIL");
  if (sample.length < 2) return 0;
  let weighted = 0;
  for (let i = 1; i < sample.length; i++) {
    const prev = sample[i - 1]!;
    const curr = sample[i]!;
    if (prev.environment !== curr.environment) continue;
    if (prev.status === curr.status) continue;
    const sameSha = Boolean(prev.git_commit_sha) && prev.git_commit_sha === curr.git_commit_sha;
    weighted += sameSha ? 1.5 : 1;
  }
  const score = (weighted / (sample.length - 1)) * 100;
  return clamp(Math.round(score * 10) / 10, 0, 100);
}

export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export function normalizeSteps(inputs: StepInput[]): Step[] {
  return inputs.map((step, index) => ({
    order: index + 1,
    action: step.action.trim(),
    data: (step.data ?? step.test_data ?? "").trim(),
    expected: (step.expected ?? step.expected_result ?? "").trim(),
    shared_step_id: step.shared_step_id ?? null,
    called_case_id: step.called_case_id ?? step.called_test_case_id ?? null,
  }));
}

export function stepsText(steps: Step[]): string {
  return steps
    .map((step) => [step.action, step.data, step.expected].filter(Boolean).join(" "))
    .join("\n");
}

export function substitute(text: string, row: Record<string, string> | null | undefined): string {
  if (!text) return "";
  if (!row) return text;
  return text.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (_, key: string) =>
    Object.prototype.hasOwnProperty.call(row, key) ? String(row[key]) : `{{${key}}}`,
  );
}

export function expandAxes(axes: Record<string, string[]>): Array<Record<string, string>> {
  const keys = Object.keys(axes).filter((key) => Array.isArray(axes[key]) && axes[key]!.length > 0);
  if (keys.length === 0) return [];
  let combos: Array<Record<string, string>> = [{}];
  for (const key of keys) {
    const next: Array<Record<string, string>> = [];
    for (const combo of combos) {
      for (const value of axes[key]!) {
        next.push({ ...combo, [key]: value });
      }
    }
    combos = next;
  }
  return combos;
}

export function configLabel(combo: Record<string, string> | null | undefined): string {
  if (!combo || Object.keys(combo).length === 0) return "default";
  return Object.keys(combo)
    .sort()
    .map((key) => `${key}=${combo[key]}`)
    .join(", ");
}

function stepBody(step: Step): Omit<Step, "order"> {
  return {
    action: step.action,
    data: step.data,
    expected: step.expected,
    shared_step_id: step.shared_step_id,
    called_case_id: step.called_case_id,
  };
}

export function diffSteps(left: Step[], right: Step[]): DiffRow[] {
  const rows: DiffRow[] = [];
  const max = Math.max(left.length, right.length);
  for (let i = 0; i < max; i++) {
    const a = left[i] ?? null;
    const b = right[i] ?? null;
    let change: DiffRow["change"] = "same";
    if (a && !b) change = "deleted";
    else if (!a && b) change = "added";
    else if (a && b && JSON.stringify(stepBody(a)) !== JSON.stringify(stepBody(b))) change = "modified";
    rows.push({ order: i + 1, change, left: a, right: b });
  }
  return rows;
}

/** Numeric order for keys such as PA-T1, PA-T2, PA-T10. Text order puts PA-T10 before PA-T2. */
export function compareTestKeys(left: string, right: string): number {
  const a = splitTestKey(left);
  const b = splitTestKey(right);
  if (a.prefix !== b.prefix) return a.prefix < b.prefix ? -1 : 1;
  if (a.number !== b.number) return a.number - b.number;
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

function splitTestKey(value: string): { prefix: string; number: number } {
  const match = /^(.*?)(\d+)$/.exec(value);
  if (!match) return { prefix: value, number: 0 };
  return { prefix: match[1] ?? value, number: Number(match[2] ?? "0") };
}

/** Forge issue panel modules should render the compact coverage view, not the workspace nav. */
export function isIssuePanel(input: { moduleKey?: unknown; extension?: { type?: unknown } | null } | null | undefined): boolean {
  if (!input) return false;
  const type = typeof input.extension?.type === "string" ? input.extension.type : "";
  const moduleKey = typeof input.moduleKey === "string" ? input.moduleKey : "";
  return type === "jira:issuePanel" || moduleKey === "tcm-issue-coverage-panel";
}

export type MarkTone = "pass" | "fail" | "blocked" | "skip" | "neutral" | "progress" | "flaky";

/** One tone per execution status. Only the current status is the selected (filled) control. */
export function executionMarkTone(status: string): MarkTone {
  if (status === "PASS") return "pass";
  if (status === "FAIL") return "fail";
  if (status === "FLAKY") return "flaky";
  if (status === "BLOCKED") return "blocked";
  if (status === "WIP") return "progress";
  if (status === "SKIPPED") return "skip";
  return "neutral";
}

export function toFtsQuery(raw: string): string {
  const terms = raw.match(/[\p{L}\p{N}_]+/gu)?.filter((term) => term.length > 0) ?? [];
  if (terms.length === 0) return "";
  return terms.map((term) => `"${term.replaceAll('"', "")}"`).join(" AND ");
}

export function parseJson<T>(value: string | null | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function extractTestKey(text: string | null | undefined): string | null {
  if (!text) return null;
  const match = text.match(/@?([A-Z][A-Z0-9]+-T\d+)/);
  return match?.[1] ?? null;
}

export interface NormalizedResult {
  test_key: string | null;
  automation_signature: string | null;
  title: string | null;
  status: "PASS" | "FAIL" | "SKIPPED" | "BLOCKED" | "FLAKY";
  duration_ms: number;
  config_label: string;
  error_message: string | null;
  stack_trace: string | null;
  dataset_row_index: number;
  executed_at: string | null;
  artifacts: Array<{ filename: string; content_type: string }>;
}

const STATUS_MAP: Record<string, NormalizedResult["status"]> = {
  PASS: "PASS",
  PASSED: "PASS",
  FAIL: "FAIL",
  FAILED: "FAIL",
  TIMEDOUT: "FAIL",
  TIMEOUT: "FAIL",
  SKIPPED: "SKIPPED",
  SKIP: "SKIPPED",
  BLOCKED: "BLOCKED",
  FLAKY: "FLAKY",
  INTERRUPTED: "BLOCKED",
};

export function normalizeStatus(value: string | null | undefined): NormalizedResult["status"] {
  const key = (value ?? "").trim().toUpperCase();
  return STATUS_MAP[key] ?? "FAIL";
}

interface PlaywrightSuite {
  title?: string;
  file?: string;
  specs?: PlaywrightSpec[];
  suites?: PlaywrightSuite[];
}

interface PlaywrightSpec {
  title?: string;
  file?: string;
  tests?: Array<{
    annotations?: Array<{ type?: string; description?: string }>;
    results?: Array<{
      status?: string;
      duration?: number;
      error?: { message?: string; stack?: string };
    }>;
  }>;
}

export function parsePlaywrightReport(report: unknown): NormalizedResult[] {
  const root = report as { suites?: PlaywrightSuite[] };
  const out: NormalizedResult[] = [];
  const walk = (suite: PlaywrightSuite, file: string) => {
    const nextFile = suite.file ?? file;
    for (const spec of suite.specs ?? []) {
      const specFile = spec.file ?? nextFile;
      for (const test of spec.tests ?? []) {
        const notes = (test.annotations ?? [])
          .map((item) => `${item.type ?? ""} ${item.description ?? ""}`)
          .join(" ");
        const title = spec.title ?? "Playwright test";
        const signature = specFile ? `${specFile}::${title}` : title;
        const result = test.results?.[test.results.length - 1];
        const blob = `${title}\n${notes}\n${signature}`;
        out.push({
          test_key: extractTestKey(blob),
          automation_signature: signature,
          title,
          status: normalizeStatus(result?.status),
          duration_ms: Math.round(result?.duration ?? 0),
          config_label: "default",
          error_message: result?.error?.message ?? null,
          stack_trace: result?.error?.stack ?? null,
          dataset_row_index: 0,
          executed_at: null,
          artifacts: [],
        });
      }
    }
    for (const child of suite.suites ?? []) walk(child, nextFile);
  };
  for (const suite of root.suites ?? []) walk(suite, "");
  return out;
}

export function parseJUnit(xml: string): NormalizedResult[] {
  const out: NormalizedResult[] = [];
  const re = /<testcase\b([^>]*?)(?:\/>|>([\s\S]*?)<\/testcase>)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(xml))) {
    const attr = attrs(match[1] ?? "");
    const body = match[2] ?? "";
    const name = attr.name ?? "testcase";
    const classname = attr.classname ?? "";
    const signature = classname ? `${classname}::${name}` : name;
    let status: NormalizedResult["status"] = "PASS";
    let error: string | null = null;
    let stack: string | null = null;
    const failure = body.match(/<(failure|error)\b([^>]*)>([\s\S]*?)<\/\1>/);
    if (failure) {
      status = "FAIL";
      const fat = attrs(failure[2] ?? "");
      error = fat.message ?? "failed";
      stack = decodeXml(failure[3] ?? "");
    } else if (/<skipped\b/.test(body)) {
      status = "SKIPPED";
    }
    const blob = `${name}\n${classname}\n${body}`;
    const seconds = Number(attr.time ?? "0");
    out.push({
      test_key: extractTestKey(blob),
      automation_signature: signature,
      title: name,
      status,
      duration_ms: Number.isFinite(seconds) ? Math.round(seconds * 1000) : 0,
      config_label: "default",
      error_message: error,
      stack_trace: stack,
      dataset_row_index: 0,
      executed_at: null,
      artifacts: [],
    });
  }
  return out;
}

function attrs(raw: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /([A-Za-z_:][\w:.-]*)\s*=\s*"([^"]*)"/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(raw))) {
    out[match[1]!] = decodeXml(match[2]!);
  }
  return out;
}

function decodeXml(value: string): string {
  return value
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&quot;", '"')
    .replaceAll("&apos;", "'")
    .replaceAll("&amp;", "&");
}

export function adfToText(node: unknown): string {
  if (!node || typeof node !== "object") return "";
  const record = node as { text?: string; content?: unknown[] };
  const own = record.text ?? "";
  const children = (record.content ?? []).map((child) => adfToText(child)).filter(Boolean);
  return [own, ...children].filter(Boolean).join("\n").trim();
}

export function textToAdf(text: string): unknown {
  return {
    type: "doc",
    version: 1,
    content: text.split("\n").map((line) => ({
      type: "paragraph",
      content: line ? [{ type: "text", text: line }] : [],
    })),
  };
}

export interface CoverageProperty {
  status: "PASSED" | "FAILED" | "IN_PROGRESS" | "UNEXECUTED" | "UNCOVERED";
  failedCount: number;
  passedCount: number;
  totalLinkedTests: number;
  hasFlakyTests: "true" | "false";
  lastExecutedAt: string | null;
}

export function rollupStatus(stepStatuses: string[]): string {
  if (stepStatuses.length === 0) return "UNEXECUTED";
  if (stepStatuses.some((status) => status === "FAIL")) return "FAIL";
  if (stepStatuses.some((status) => status === "BLOCKED")) return "BLOCKED";
  if (stepStatuses.some((status) => status === "WIP" || status === "UNEXECUTED")) {
    if (stepStatuses.some((status) => status !== "UNEXECUTED")) return "WIP";
    return "UNEXECUTED";
  }
  if (stepStatuses.every((status) => status === "SKIPPED")) return "SKIPPED";
  return "PASS";
}
